import {
  mortgagePrincipalOutstanding,
  mortgageFormSectionCalculator,
} from '../../utility/mortgageCalculator';
import { getTableSum } from '../../utility/getTableSum';
import { isValue } from '../../utility/isValue';
import { yearlyPercentFromYearlyValue } from '../../utility/percentageConverters';
import FormUIOptions from '../../components/formBuilder/formElements/formUIOptions';

export default {
  title: 'Flip Analysis',
  description: 'Analysis of the costs and returns of a flip property.',
  schema: [
    {
      sectionType: 'chart',
      sectionLabel: 'Purchase & Rehab',
      schema: [
        {
          type: FormUIOptions.Money,
          label: 'Asking Price',
          name: 'askingPrice',
        },
        {
          type: FormUIOptions.Money,
          label: 'Purchase Price',
          name: 'purchasePrice',
        },
        {
          type: FormUIOptions.Money,
          label: 'Insurance Premium',
          name: 'cmhcPremium',
          condition: [
            {
              field: 'shouldCalculateCMHC',
              value: true,
            },
            {
              field: 'mortgageInterestOnly',
              value: false,
            },
            {
              field: 'propertyInCanada',
              value: [undefined, null, 1, ''],
            },
          ],
          formula: (cmhcPremium) => ['+', cmhcPremium],
        },
        {
          type: FormUIOptions.Money,
          label: 'Purchase Financing',
          description: 'The amount of financing you are using to purchase.',
          name: 'totalMortgageAmount',
          derivedFrom: ['totalMortgageAmount', 'cashPurchase'],
          calcFunction: (totalMortgageAmount, cashPurchase) => {
            if (cashPurchase) {
              return 0;
            }
            return totalMortgageAmount;
          },
          formula: (totalMortgageAmount) => ['-', totalMortgageAmount],
        },
        {
          type: FormUIOptions.Money,
          label: 'Down Payment',
          name: 'downPaymentAmount',
          emphasize: true,
          groupStart: true,
          total: true,
          derivedFrom: ['downPaymentAmount', 'purchasePrice', 'cashPurchase'],
          calcFunction: (downPaymentAmount, purchasePrice, cashPurchase) => {
            if (cashPurchase) {
              return purchasePrice;
            }
            return downPaymentAmount;
          },
          formula: (downPaymentAmount) => ['=', downPaymentAmount],
        },
        {
          type: FormUIOptions.Money,
          label: 'Closing Costs',
          name: 'totalCashRequiredToClose',
          dropDown: true,
          formula: (totalCashRequiredToClose) => [
            '+',
            totalCashRequiredToClose,
          ],
        },
        {
          type: FormUIOptions.Table,
          label: '',
          name: 'closingCosts',
          dropDownDependency: 'totalCashRequiredToClose',
          condition: [
            {
              field: 'cashPurchase',
              value: false,
            },
            {
              field: 'enterTotalClosingCosts',
              value: false,
            },
          ],
          schema: [
            {
              type: FormUIOptions.Text,
              name: 'closingCostName',
              label: 'Closing Cost',
              dropDownDependency: 'totalCashRequiredToClose',
            },
            {
              type: FormUIOptions.Money,
              label: 'Amount',
              layout: 'default',
              name: 'closingCostAmount',
              unit: '$',
              dropDownDependency: 'totalCashRequiredToClose',
            },
          ],
        },
        {
          type: FormUIOptions.Money,
          label: 'Rehab Costs',
          name: 'totalRehabCosts',
          dropDown: true,
          formula: (cost) => ['+', cost],
        },
        {
          type: FormUIOptions.Table,
          label: '',
          name: 'additionalInvestment',
          dropDownDependency: 'totalRehabCosts',
          schema: [
            {
              type: FormUIOptions.Text,
              name: 'additionalInvestmentItem',
              label: 'Closing Cost',
              dropDownDependency: 'totalRehabCosts',
            },
            {
              type: FormUIOptions.Money,
              label: 'Amount',
              layout: 'default',
              name: 'additionalInvestmentValue',
              unit: '$',
              dropDownDependency: 'totalRehabCosts',
            },
          ],
        },
        {
          type: FormUIOptions.Money,
          label: 'Rehab Financing',
          name: 'totalRehabFinancingAmount',
          formula: (totalRehabFinancingAmount) => [
            '-',
            totalRehabFinancingAmount,
          ],
          condition: {
            field: 'isRehabFinanced',
            value: true,
          },
        },
        {
          type: FormUIOptions.Money,
          label: 'Total Cash Needed',
          total: true,
          groupStart: true,
          emphasize: true,
          name: 'totalCashInvestedOnPurchase',
          derivedFrom: [
            'downPaymentAmount',
            'totalCashRequiredToClose',
            'totalRehabCosts',
            'totalRehabFinancingAmount',
            'isRehabFinanced',
          ],
          calcFunction: (
            downPaymentAmount,
            totalCashRequiredToClose,
            totalRehabCosts,
            totalRehabFinancingAmount,
            isRehabFinanced
          ) => {
            const total =
              (downPaymentAmount || 0) +
              (totalCashRequiredToClose || 0) +
              (totalRehabCosts || 0);

            if (isRehabFinanced) {
              return total - (totalRehabFinancingAmount || 0);
            }

            return total;
          },
          formula: (totalCashInvestedOnPurchase) => [
            '=',
            totalCashInvestedOnPurchase,
          ],
        },
      ],
      chartSchema: {
        title: 'Purchase Cost',
        type: 'pie',
        schemas: [
          {
            title: 'Purchase Cost',
            schema: [
              {
                type: FormUIOptions.Money,
                label: 'Down Payment',
                name: 'downPaymentAmount',
                unit: '$',
              },
              {
                type: FormUIOptions.Money,
                label: 'Closing Cost',
                name: 'totalCashRequiredToClose',
                unit: '$',
              },
            ],
          },
          {
            title: 'Closing Costs',
            schema: [
              {
                type: FormUIOptions.Table,
                label: 'Closing Costs',
                name: 'closingCosts',
                schema: [
                  {
                    type: FormUIOptions.Text,
                    name: 'closingCostName',
                    label: 'Closing Cost',
                  },
                  {
                    type: FormUIOptions.Money,
                    name: 'closingCostAmount',
                    unit: '$',
                  },
                ],
              },
            ],
            condition: {
              field: 'enterTotalClosingCosts',
              value: false,
            },
          },
          {
            title: 'Rehab Costs',
            schema: [
              {
                type: FormUIOptions.Table,
                label: 'Rehab Costs',
                name: 'additionalInvestment',
                schema: [
                  {
                    type: FormUIOptions.Text,
                    name: 'additionalInvestmentItem',
                    label: 'Rehab Cost',
                  },
                  {
                    type: FormUIOptions.Money,
                    name: 'additionalInvestmentValue',
                    unit: '$',
                  },
                ],
              },
            ],
          },
        ],
      },
    },
    {
      sectionType: 'twoColumn',
      leftSchema: {
        sectionLabel: 'Financing Details',
        schema: [
          {
            type: FormUIOptions.Money,
            label: 'Mortgage Amount',
            name: 'totalMortgageAmount',
            dropDown: true,
          },
          {
            type: FormUIOptions.Money,
            label: 'Mortgage Amount',
            layout: 'default',
            name: 'mortgageAmount',
            unit: '$',
            dropDownDependency: 'totalMortgageAmount',
            derivedFrom: ['purchasePrice', 'downPayment'],
            calcFunction: (purchasePrice, downPayment) => {
              if (isValue(purchasePrice, downPayment)) {
                const result = purchasePrice - downPayment;

                return result;
              } else {
                return null;
              }
            },
            condition: {
              field: 'cashPurchase',
              value: false,
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Insurance Premium',
            name: 'cmhcPremium',
            dropDownDependency: 'totalMortgageAmount',
            condition: [
              {
                field: 'shouldCalculateCMHC',
                value: true,
              },
              {
                field: 'cashPurchase',
                value: false,
              },
              {
                field: 'propertyInCanada',
                value: [undefined, null, 1, ''],
              },
            ],
          },
          {
            type: FormUIOptions.YesNo,
            label: 'Interest-Only',
            layout: 'default',
            name: 'mortgageInterestOnly',
            dropDownDependency: 'totalMortgageAmount',
            condition: [
              {
                field: 'hasSecondMortgage',
                value: true,
              },
              {
                field: 'cashPurchase',
                value: false,
              },
            ],
          },
          {
            type: FormUIOptions.Number,
            label: 'Interest Rate',
            layout: 'default',
            name: 'interestRate',
            dropDownDependency: 'totalMortgageAmount',
            unit: '%',
            condition: {
              field: 'cashPurchase',
              value: false,
            },
          },
          {
            type: FormUIOptions.YesNo,
            label: 'Interest paid monthly?',
            layout: 'default',
            name: 'interestOnlyMonthly',
            dropDownDependency: 'totalMortgageAmount',
            condition: [
              {
                field: 'mortgageInterestOnly',
                value: true,
              },
              {
                field: 'cashPurchase',
                value: false,
              },
            ],
          },
          {
            type: FormUIOptions.Number,
            label: 'Loan Period',
            layout: 'default',
            unit: 'years',
            name: 'interestOnlyLoanPeriod',
            dropDownDependency: 'totalMortgageAmount',
            condition: [
              {
                field: 'mortgageInterestOnly',
                value: true,
              },
              {
                field: 'cashPurchase',
                value: false,
              },
            ],
          },
          {
            type: FormUIOptions.Number,
            label: 'Amortization',
            layout: 'default',
            name: 'amortization',
            unit: 'years',
            dropDownDependency: 'totalMortgageAmount',
            condition: [
              {
                field: 'mortgageInterestOnly',
                value: !true,
              },
              {
                field: 'cashPurchase',
                value: false,
              },
            ],
          },
          {
            type: FormUIOptions.Number,
            label: 'Loan To Value',
            layout: 'default',
            name: 'loanToValueFirstMortgage',
            unit: '%',
            dropDownDependency: 'totalMortgageAmount',
            derivedFrom: ['mortgageAmount', 'purchasePrice'],
            calcFunction: (mortgageAmount, purchasePrice) => {
              if (isValue(mortgageAmount, purchasePrice)) {
                const result = (mortgageAmount / purchasePrice) * 100;
                return result;
              } else {
                return null;
              }
            },
            condition: [
              {
                field: 'cashPurchase',
                value: false,
              },
            ],
          },
          {
            type: FormUIOptions.Money,
            label: 'Monthly Payment',
            layout: 'default',
            name: 'monthlyPayment',
            unit: '$',
            groupEnd: true,
            dropDownDependency: 'totalMortgageAmount',
            derivedFrom: [
              'interestRate',
              'mortgageAmount',
              'amortization',
              'mortgageInterestOnly',
              'interestOnlyMonthly',
              'shouldCalculateCMHC',
              'cmhcPremium',
              'propertyInCanada',
            ],
            calcFunction: (
              interestRate,
              mortgageAmount,
              amortization,
              mortgageInterestOnly,
              interestOnlyMonthly,
              shouldCalculateCMHC,
              cmhcPremium,
              propertyInCanada
            ) => {
              let mortgageTotal = mortgageAmount;
              if (
                shouldCalculateCMHC &&
                cmhcPremium &&
                !mortgageInterestOnly &&
                propertyInCanada !== 0
              ) {
                mortgageTotal += cmhcPremium;
              }

              return mortgageFormSectionCalculator(
                mortgageTotal,
                interestRate,
                mortgageInterestOnly,
                interestOnlyMonthly,
                amortization
              );
            },
            condition: [
              {
                field: 'cashPurchase',
                value: false,
              },
            ],
          },
          //type table is for listing items that have been entered in a customizable table / array of objects
          {
            type: FormUIOptions.Table,
            label: 'Additional Financing',
            name: 'purchaseAdditionalFinancing',
            dropDownDependency: 'totalMortgageAmount',
            condition: [
              {
                field: 'cashPurchase',
                value: false,
              },
            ],
            schema: [
              {
                type: FormUIOptions.Text,
                name: 'loanNameTable',
                label: 'Loan Name',
                dropDownDependency: 'totalMortgageAmount',
              },
              {
                type: FormUIOptions.Money,
                label: 'Loan Amount',
                layout: 'default',
                name: 'mortgageAmountTable',
                unit: '$',
                dropDownDependency: 'totalMortgageAmount',
              },
              {
                type: FormUIOptions.YesNo,
                label: 'Interest-Only',
                layout: 'default',
                name: 'mortgageInterestOnlyTable',
                dropDownDependency: 'totalMortgageAmount',
                condition: {
                  field: 'mortgageInterestOnlyTable',
                  value: true,
                },
              },
              {
                type: FormUIOptions.Number,
                label: 'Interest Rate',
                layout: 'default',
                name: 'interestRateTable',
                dropDownDependency: 'totalMortgageAmount',
                unit: '%',
              },
              {
                type: FormUIOptions.YesNo,
                label: 'Interest paid monthly?',
                layout: 'default',
                name: 'mortgageInterestOnlyTable',
                dropDownDependency: 'totalMortgageAmount',
                condition: {
                  field: 'mortgageInterestOnlyTable',
                  value: true,
                },
              },
              {
                type: FormUIOptions.Number,
                label: 'Loan Period',
                layout: 'default',
                unit: 'years',
                name: 'interestOnlyLoanPeriodTable',
                dropDownDependency: 'totalMortgageAmount',
                condition: {
                  field: 'mortgageInterestOnlyTable',
                  value: true,
                },
              },
              {
                type: FormUIOptions.Number,
                label: 'Amortization',
                layout: 'default',
                name: 'amortizationTable',
                unit: 'years',
                dropDownDependency: 'totalMortgageAmount',
                condition: {
                  field: 'mortgageInterestOnlyTable',
                  value: false,
                },
              },
              {
                type: FormUIOptions.Number,
                label: 'Loan To Value',
                layout: 'default',
                name: 'loanToValueTable',
                unit: 'years',
                dropDownDependency: 'totalMortgageAmount',
                mainValue: 'percent',
                displayPercentFrom: ['mortgageAmountTable', 'purchasePrice'],
                displayPercentFunction: (
                  mortgageAmountTable,
                  purchasePrice
                ) => {
                  return (
                    yearlyPercentFromYearlyValue(mortgageAmountTable, [
                      purchasePrice,
                    ]) || ' -'
                  );
                },
              },
              {
                type: FormUIOptions.Money,
                label: 'Monthly Payment',
                layout: 'default',
                name: 'monthlyPaymentTable',
                groupEnd: true,
                unit: '$',
                dropDownDependency: 'totalMortgageAmount',
              },
            ],
          },
          {
            type: FormUIOptions.Money,
            label: 'Rehab Financing',
            name: 'totalRehabFinancingAmount',
            dropDown: true,
            condition: {
              field: 'isRehabFinanced',
              value: true,
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Loan Amount',
            layout: 'default',
            name: 'mortgageAmountRehab',
            unit: '$',
            dropDownDependency: 'totalRehabFinancingAmount',
          },
          {
            type: FormUIOptions.YesNo,
            label: 'Interest-Only',
            layout: 'default',
            name: 'mortgageInterestOnlyRehab',
            dropDownDependency: 'totalRehabFinancingAmount',
            condition: {
              field: 'mortgageInterestOnlyRehab',
              value: true,
            },
          },
          {
            type: FormUIOptions.Number,
            label: 'Interest Rate',
            layout: 'default',
            name: 'interestRateRehab',
            dropDownDependency: 'totalRehabFinancingAmount',
            unit: '%',
          },
          {
            type: FormUIOptions.YesNo,
            label: 'Interest paid monthly?',
            layout: 'default',
            name: 'mortgageInterestOnlyRehab',
            dropDownDependency: 'totalRehabFinancingAmount',
            condition: {
              field: 'mortgageInterestOnlyRehab',
              value: true,
            },
          },
          {
            type: FormUIOptions.Number,
            label: 'Loan Period',
            layout: 'default',
            unit: 'years',
            name: 'interestOnlyLoanPeriodRehab',
            dropDownDependency: 'totalRehabFinancingAmount',
            condition: {
              field: 'mortgageInterestOnlyRehab',
              value: true,
            },
          },
          {
            type: FormUIOptions.Number,
            label: 'Amortization',
            layout: 'default',
            name: 'amortizationRehab',
            unit: 'years',
            dropDownDependency: 'totalRehabFinancingAmount',
            condition: {
              field: 'mortgageInterestOnlyRehab',
              value: false,
            },
          },
          {
            type: FormUIOptions.Number,
            label: 'Loan To Value',
            layout: 'default',
            name: 'loanToValueRehab',
            unit: '%',
            dropDownDependency: 'totalRehabFinancingAmount',
            derivedFrom: ['mortgageAmountRehab', 'purchasePrice'],
            calcFunction: (mortgageAmountTable, purchasePrice) => {
              return (
                yearlyPercentFromYearlyValue(mortgageAmountTable, [
                  purchasePrice,
                ]) || ' -'
              );
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Monthly Payment',
            layout: 'default',
            name: 'monthlyPaymentRehab',
            derivedFrom: [
              'mortgageAmountRehab',
              'interestRateRehab',
              'mortgageInterestOnlyRehab',
              'interestOnlyMonthlyRehab',
              'amortizationRehab',
            ],
            calcFunction: (
              mortgageAmountRehab,
              interestRateRehab,
              mortgageInterestOnlyRehab,
              interestOnlyMonthlyRehab,
              amortizationRehab
            ) => {
              return mortgageFormSectionCalculator(
                mortgageAmountRehab,
                interestRateRehab,
                mortgageInterestOnlyRehab,
                interestOnlyMonthlyRehab,
                amortizationRehab
              );
            },
            groupEnd: true,
            unit: '$',
            dropDownDependency: 'totalRehabFinancingAmount',
          },
          {
            type: FormUIOptions.Table,
            label: '',
            name: 'rehabCostFinancing',
            dropDownDependency: 'totalRehabFinancingAmount',
            condition: [
              {
                field: 'cashPurchase',
                value: false,
              },
            ],
            schema: [
              {
                type: FormUIOptions.Text,
                name: 'loanNameTable',
                label: 'Loan Name',
                dropDownDependency: 'totalRehabFinancingAmount',
              },
              {
                type: FormUIOptions.Money,
                label: 'Loan Amount',
                layout: 'default',
                name: 'mortgageAmountTable',
                unit: '$',
                dropDownDependency: 'totalRehabFinancingAmount',
              },
              {
                type: FormUIOptions.YesNo,
                label: 'Pay off on refinance?',
                name: 'payOffOnRefinanceTable',
                dropDownDependency: 'totalRehabFinancingAmount',
              },
              {
                type: FormUIOptions.YesNo,
                label: 'Interest-Only',
                layout: 'default',
                name: 'mortgageInterestOnlyTable',
                dropDownDependency: 'totalRehabFinancingAmount',
                condition: {
                  field: 'mortgageInterestOnlyTable',
                  value: true,
                },
              },
              {
                type: FormUIOptions.Number,
                label: 'Interest Rate',
                layout: 'default',
                name: 'interestRateTable',
                dropDownDependency: 'totalRehabFinancingAmount',
                unit: '%',
              },
              {
                type: FormUIOptions.YesNo,
                label: 'Interest paid monthly?',
                layout: 'default',
                name: 'mortgageInterestOnlyTable',
                dropDownDependency: 'totalRehabFinancingAmount',
                condition: {
                  field: 'mortgageInterestOnlyTable',
                  value: true,
                },
              },
              {
                type: FormUIOptions.Number,
                label: 'Loan Period',
                layout: 'default',
                unit: 'years',
                name: 'interestOnlyLoanPeriodTable',
                dropDownDependency: 'totalRehabFinancingAmount',
                condition: {
                  field: 'mortgageInterestOnlyTable',
                  value: true,
                },
              },
              {
                type: FormUIOptions.Number,
                label: 'Amortization',
                layout: 'default',
                name: 'amortizationTable',
                unit: 'years',
                dropDownDependency: 'totalRehabFinancingAmount',
                condition: {
                  field: 'mortgageInterestOnlyTable',
                  value: false,
                },
              },
              {
                type: FormUIOptions.Number,
                label: 'Loan To Value',
                layout: 'default',
                name: 'loanToValueTable',
                unit: 'years',
                dropDownDependency: 'totalRehabFinancingAmount',
                mainValue: 'percent',
                displayPercentFrom: ['mortgageAmountTable', 'purchasePrice'],
                displayPercentFunction: (
                  mortgageAmountTable,
                  purchasePrice
                ) => {
                  return (
                    yearlyPercentFromYearlyValue(mortgageAmountTable, [
                      purchasePrice,
                    ]) || ' -'
                  );
                },
              },
              {
                type: FormUIOptions.Money,
                label: 'Monthly Payment',
                layout: 'default',
                name: 'monthlyPaymentTable',
                groupEnd: true,
                unit: '$',
                dropDownDependency: 'totalRehabFinancingAmount',
              },
            ],
          },
          {
            type: FormUIOptions.Number,
            label: 'Loan to Value (LTV)',
            name: 'loanToValue',
            description:
              'The loan amount as a percentage of the property value',
            tooltipHeading: 'Loan to Value (LTV)',
            tooltipFormula: ['LTV', '=', 'Loan Amount', '/', 'Property Value'],
            unit: '%',
            derivedFrom: [
              'totalMortgageAmount',
              'purchasePrice',
              'totalRehabFinancingAmount',
              'isRehabFinanced',
            ],
            calcFunction: (
              totalMortgageAmount,
              purchasePrice,
              totalRehabFinancingAmount,
              isRehabFinanced
            ) => {
              if (!isValue(totalMortgageAmount, purchasePrice)) return null;

              if (!isRehabFinanced) {
                return (
                  Math.round(
                    (totalMortgageAmount / purchasePrice) * 100 * 100
                  ) / 100 || 0
                );
              } else {
                return Math.round(
                  (((totalMortgageAmount + (totalRehabFinancingAmount || 0)) /
                    purchasePrice) *
                    100 *
                    100) /
                    100 || 0
                );
              }
            },
          },
          {
            type: FormUIOptions.Money,
            label: 'Monthly Payment',
            layout: 'default',
            name: 'monthlyDebtService',
            emphasize: true,
            unit: '$',
            derivedFrom: [
              'totalMonthlyMortgagePayment',
              'cashPurchase',
              'totalRehabFinancingMonthlyPayment',
              'isRehabFinanced',
            ],
            calcFunction: (
              totalMonthlyMortgagePayment,
              cashPurchase,
              totalRehabFinancingMonthlyPayment,
              isRehabFinanced
            ) => {
              if (cashPurchase) {
                return isRehabFinanced ? totalRehabFinancingMonthlyPayment : 0;
              }

              if (!isRehabFinanced) {
                return cashPurchase ? 0 : totalMonthlyMortgagePayment;
              }

              return (
                totalMonthlyMortgagePayment +
                (totalRehabFinancingMonthlyPayment || 0)
              );
            },
          },
        ],
      },
      rightSchema: {
        sectionLabel: 'Sale & Profit',
        schema: [
          {
            label: 'After Repair Value',
            name: 'newMarketValue',
            type: FormUIOptions.Money,
            unit: '$',
          },
          {
            label: 'Selling Costs',
            name: 'sellingCosts',
            description:
              'The costs associated with selling the property. Based on the market value and estimated sale cost percentage.',
            type: FormUIOptions.Money,
            unit: '$',
            derivedFrom: ['saleCostPercent', 'newMarketValue'],
            calcFunction: (saleCostPercent, newMarketValue) => {
              if (!isValue(saleCostPercent, newMarketValue)) return null;
              return newMarketValue * (saleCostPercent / 100);
            },
            formula: (sellingCosts) => ['-', sellingCosts],
          },
          {
            label: 'Sale Proceeds',
            name: 'saleProceeds',
            type: FormUIOptions.Money,
            unit: '$',
            total: true,
            emphasize: true,
            derivedFrom: ['newMarketValue', 'sellingCosts'],
            calcFunction: (newMarketValue, sellingCosts) => {
              if (!isValue(newMarketValue, sellingCosts)) return null;
              return newMarketValue - sellingCosts;
            },
            formula: (saleProceeds) => ['=', saleProceeds],
          },
          //need to figure out the remaining loan balance after the payments during the holding period, will depend on if the loans are interest only, need to loop through each loan
          {
            label: 'Loan Repayment',
            name: 'loanRepayment',
            type: FormUIOptions.Money,
            description:
              'The total loan amount from all loans that needs to be repaid, after accounting for payments made during the holding period.',
            unit: '$',
            derivedFrom: [
              //other details
              'rehabLength',
              'cashPurchase',
              'isRehabFinanced',

              //first mortgage
              'mortgageAmount',
              'mortgageInterestOnly',
              'interestRate',
              'monthlyPayment',

              //additional financing
              'purchaseAdditionalFinancing',

              //rehab financing
              'mortgageAmountRehab',
              'mortgageInterestOnlyRehab',
              'interestRateRehab',
              'monthlyPaymentRehab',

              //rehab financing additional
              'rehabCostFinancing',
            ],
            calcFunction: (
              rehabLength,
              cashPurchase,
              isRehabFinanced,
              mortgageAmount,
              mortgageInterestOnly,
              interestRate,
              monthlyPayment,
              purchaseAdditionalFinancing,
              mortgageAmountRehab,
              mortgageInterestOnlyRehab,
              interestRateRehab,
              monthlyPaymentRehab,
              rehabCostFinancing
            ) => {
              let purchaseRepaymentTotal = 0;
              let rehabRepaymentTotal = 0;

              if (!cashPurchase) {
                purchaseRepaymentTotal = mortgageInterestOnly
                  ? mortgageAmount
                  : mortgagePrincipalOutstanding(
                      interestRate,
                      mortgageAmount,
                      monthlyPayment,
                      rehabLength
                    );

                if (purchaseAdditionalFinancing) {
                  purchaseAdditionalFinancing.forEach((loan) => {
                    purchaseRepaymentTotal += loan.mortgageInterestOnlyTable
                      ? loan.mortgageAmountTable
                      : mortgagePrincipalOutstanding(
                          loan.interestRateTable,
                          loan.mortgageAmountTable,
                          loan.monthlyPaymentTable,
                          rehabLength
                        );
                  });
                }
              }

              if (isRehabFinanced) {
                rehabRepaymentTotal = mortgageInterestOnlyRehab
                  ? mortgageAmountRehab
                  : mortgagePrincipalOutstanding(
                      interestRateRehab,
                      mortgageAmountRehab,
                      monthlyPaymentRehab,
                      rehabLength
                    );

                if (rehabCostFinancing) {
                  rehabCostFinancing.forEach((loan) => {
                    rehabRepaymentTotal += loan.mortgageInterestOnlyTable
                      ? loan.mortgageAmountTable
                      : mortgagePrincipalOutstanding(
                          loan.interestRateTable,
                          loan.mortgageAmountTable,
                          loan.monthlyPaymentTable,
                          rehabLength
                        );
                  });
                }
              }

              return purchaseRepaymentTotal + rehabRepaymentTotal;
            },
            formula: (loanRepayment) => ['-', loanRepayment],
          },
          {
            label: 'Carrying Costs',
            name: 'totalCarryingCosts',
            type: FormUIOptions.Money,
            description:
              'The total costs associated with holding the property during the rehab period.',
            unit: '$',
            formula: (totalCarryingCosts) => ['-', totalCarryingCosts],
          },
          {
            label: 'Cash Needed',
            name: 'totalCashInvestedOnPurchase',
            type: FormUIOptions.Money,
            formula: (totalCashInvestedOnPurchase) => [
              '-',
              totalCashInvestedOnPurchase,
            ],
          },
          {
            label: 'Total Profit',
            name: 'totalProfitFlip',
            type: FormUIOptions.Money,
            total: true,
            emphasize: true,
            dynamic: true,
            derivedFrom: [
              'saleProceeds',
              'loanRepayment',
              'totalCarryingCosts',
              'totalCashInvestedOnPurchase',
            ],
            calcFunction: (
              saleProceeds,
              loanRepayment,
              totalCarryingCosts,
              totalCashInvestedOnPurchase
            ) => {
              if (!isValue(saleProceeds)) return null;

              return (
                saleProceeds -
                (loanRepayment || 0) -
                (totalCarryingCosts || 0) -
                (totalCashInvestedOnPurchase || 0)
              );
            },
          },
        ],
      },
    },
    {
      sectionType: 'twoColumn',
      leftSchema: {
        sectionLabel: 'Carrying Costs',
        schema: [
          {
            label: 'Monthly Loan Payments',
            description: 'The total monthly payments for all loans.',
            name: 'monthlyLoanPaymentsAll',
            type: FormUIOptions.Money,
          },
          {
            label: 'Monthly Expenses',
            name: 'monthlyCarryingExpensesNoLoan',
            type: FormUIOptions.Money,
            dropDown: true,
            derivedFrom: [
              'carryingExpenses',
              'carryingExpensesNotItemized',
              'enterTotalCarryingExpenses',
            ],
            calcFunction: (
              carryingExpenses,
              carryingExpensesNotItemized,
              enterTotalCarryingExpenses
            ) => {
              if (!enterTotalCarryingExpenses) {
                return getTableSum(carryingExpenses, 'expenseAmount');
              }
              return carryingExpensesNotItemized;
            },
            formula: (monthlyCarryingExpensesNoLoan) => [
              '+',
              monthlyCarryingExpensesNoLoan,
            ],
          },
          {
            type: FormUIOptions.Table,
            label: '',
            name: 'carryingExpenses',
            dropDownDependency: 'monthlyCarryingExpensesNoLoan',
            condition: [
              {
                field: 'enterTotalCarryingExpenses',
                value: false,
              },
            ],
            schema: [
              {
                type: FormUIOptions.Text,
                name: 'expenseName',
                label: 'Name',
                dropDownDependency: 'monthlyCarryingExpensesNoLoan',
              },
              {
                type: FormUIOptions.UnitMoney,
                label: 'Amount',
                layout: 'default',
                name: 'expenseAmount',
                dropDownDependency: 'monthlyCarryingExpensesNoLoan',
              },
            ],
          },
          {
            label: 'Total Monthly Cost',
            name: 'monthlyTotalCarryingCosts',
            type: FormUIOptions.Money,
            total: true,
            derivedFrom: ['totalCarryingCosts', 'rehabLength'],
            calcFunction: (totalCarryingCosts, rehabLength) => {
              if (!isValue(totalCarryingCosts, rehabLength)) return null;
              return totalCarryingCosts / rehabLength;
            },
            formula: (monthlyTotalCarryingCosts) => [
              '=',
              monthlyTotalCarryingCosts,
            ],
          },
          {
            type: FormUIOptions.Number,
            unit: ' months',
            label: 'Rehab Length',
            name: 'rehabLength',
            decimalPlaces: 0,
            formula: (rehabLength) => ['x', rehabLength],
          },
          {
            label: 'Total Carrying Costs',
            name: 'totalCarryingCosts',
            emphasize: true,
            total: true,
            groupEnd: true,
            type: FormUIOptions.Money,
            formula: (totalCarryingCosts) => ['=', totalCarryingCosts],
          },
        ],
      },
      rightSchema: {
        sectionLabel: 'Investment Metrics',
        annualValue: true,
        schema: [
          {
            type: FormUIOptions.Number,
            unit: '%',
            label: 'Return on Investment',
            tooltipHeading: 'Return on Investment',
            description:
              'The return on investment as a percentage of the total cash invested.',
            tooltipFormula: [
              'ROI',
              '=',
              'Total Profit',
              '/',
              '(Total Cash Invested',
              '+',
              'Total Carrying Costs',
              ')',
            ],
            emphasize: true,
            dynamic: true,
            name: 'flipROI',
            derivedFrom: [
              'totalProfitFlip',
              'totalCarryingCosts',
              'totalCashInvestedOnPurchase',
            ],
            calcFunction: (
              totalProfitFlip,
              totalCarryingCosts,
              totalCashInvestedOnPurchase
            ) => {
              if (!isValue(totalProfitFlip, totalCashInvestedOnPurchase))
                return null;
              return (
                (totalProfitFlip /
                  ((totalCarryingCosts || 0) + totalCashInvestedOnPurchase)) *
                100
              );
            },
          },
          {
            type: FormUIOptions.Number,
            unit: '%',
            label: 'Annualized ROI',
            tooltipHeading: 'Annualized ROI',
            description:
              'The return on investment as a percentage of the total cash invested, annualized.',
            tooltipFormula: [
              'Annualized ROI',
              '=',
              'ROI',
              '*',
              '12',
              '/',
              'Rehab Length',
            ],
            name: 'flipROIAnnualized',
            derivedFrom: ['flipROI', 'rehabLength'],
            calcFunction: (flipROI, rehabLength) => {
              if (!isValue(flipROI, rehabLength)) return null;
              return (flipROI * 12) / rehabLength;
            },
          },
        ],
      },
    },
  ],
};
